import React, { useState, useEffect, useMemo } from 'react';
import { MOCK_CLIENTS } from '../constants';
import { Briefcase, Building2, Calendar, ChevronRight, Plus, X, Sparkles, Send, MapPin, Zap, Star, ShieldCheck, Mail, Users, User as UserIcon, ArrowUpRight, Copy, Loader2, Filter, LayoutTemplate, Layers, Play, Clock, Trash2, Eye, FileText, Tag, Search, ArrowRight, AlertCircle, MessageCircle, PhoneCall } from 'lucide-react';
import { Vacancy, VacancyStep, User, UserRole, Client } from '../types';
import { generateVacancySuggestions, parseVacancyText } from '../services/geminiService';
import { supabase_insert_vacancy, supabase_delete_vacancy, supabase_update, supabase } from '../services/supabaseService';
import VacancyCard from './VacancyCard';
import SupportSchedulerModal from './SupportSchedulerModal';
import { searchTextIncludes } from '../utils/searchText';
import { isPortalArchivedVacancy } from '../utils/clientPortalGuards';

interface VacancyListProps {
  onSelect: (vacancy: Vacancy, initialStep?: VacancyStep, isNew?: boolean) => void;
  user?: User | null;
  vacancies: Vacancy[]; // Received from parent
  clients?: Client[]; // Clients for selection
  candidates?: any[]; // For calculating phase counts
  isLoading?: boolean | number;
  onCreate?: () => void;
  onDelete?: (idOrIds: string | string[]) => void;
  onUpdate?: (vacancy: Vacancy) => void;
  /** Klantportaal: parent + werkplek-UUIDs (zelfde als supabase_get_vacancies). */
  portalAllowedClientIds?: string[];
}

export type VacancyLifecycleStatus = 'Concept' | 'Doing' | 'Open' | 'Gesloten' | 'Archief';

export const VACANCY_LIFECYCLE_TABS: VacancyLifecycleStatus[] = ['Concept', 'Open', 'Doing', 'Gesloten', 'Archief'];

/** Klantportaal: geen Concept/Archief-tab; gearchiveerde vacatures worden niet getoond. */
export const PORTAL_VACANCY_LIFECYCLE_TABS: VacancyLifecycleStatus[] = ['Open', 'Doing', 'Gesloten'];

export const getVacancyLifecycleStatus = (v: any): VacancyLifecycleStatus => {
    // Source of truth (legacy-safe):
    // - Some environments store lifecycle in `data.lifecycleStatus`
    // - Some store it in the `status` column
    // - Draft/Concept is also strongly implied when not published to site.
    const raw = (v?.data?.lifecycleStatus || v?.status || 'Concept') as any;
    const l = String(raw).toLowerCase();

    if (['afgerond', 'complete', 'succesvol', 'plaatsing', 'gesloten', 'closed'].includes(l)) return 'Gesloten';
    if (['done', 'afwezen', 'afgewezen', 'ingetrokken', 'archief', 'archive'].includes(l) || v.data?.isClosed === true) return 'Archief';

    // Recruiter is actief bezig met de vacature (intake/research/persona/strategy/sourcing/templates etc. — alles vóór Open).
    if (['doing', 'in_progress', 'in progress', 'in behandeling', 'bezig'].includes(l)) return 'Doing';

    // If it's explicitly marked as concept/draft anywhere, keep it Concept.
    if (['concept', 'draft'].includes(l)) return 'Concept';

    // Actieve werving (kolom/status "Open") is los van `publishedToSite` (alleen carrière-site).
    // Zonder deze regel worden "Open" vacatures als UI-"Concept" geclassificeerd en verdwijnen ze
    // uit de standaard "Open"-tab (vooral zichtbaar in het klantportaal).
    if (['open', 'live'].includes(l)) return 'Open';

    // If NOT published, treat the in-flow steps as Doing (recruiter is bezig). Pure "Concept" geldt
    // alleen wanneer er nog niets gestart is.
    if (v?.data?.publishedToSite !== true) {
        const startedFlow =
            !!v?.status && !['concept', 'draft', 'open'].includes(l) ||
            !!v?.data?.lifecycleStatus;
        return startedFlow ? 'Doing' : 'Concept';
    }

    // Otherwise it's live/open.
    return 'Open';
};

/** Klantportaal: tabbladen — Open = alle actieve fasen; Doing = expliciet «Doing»; Gesloten/Archief strikt. */
function portalLifecycleMatchesVacancyTab(v: any, tab: VacancyLifecycleStatus): boolean {
    const cur = getVacancyLifecycleStatus(v);
    switch (tab) {
      case 'Open':
        return cur === 'Open' || cur === 'Doing' || cur === 'Concept';
      case 'Doing':
        return cur === 'Doing';
      case 'Gesloten':
        return cur === 'Gesloten';
      case 'Archief':
        return cur === 'Archief';
      default:
        return false;
    }
}

/** CRM bedrijvenlijst: spring naar vacatures met deze klantnaam in het filter. */
const VACANCIES_CRM_CLIENT_NAME_SESSION_KEY = 'wt:vacancies_filter_crm_client_name';

function buildVacancyLifecycleUpdate(
  vacancy: Vacancy,
  newStatus: VacancyLifecycleStatus
): { updatedData: Record<string, unknown>; dbStatus: Vacancy['status']; updatedVacancy: Vacancy } {
  const updatedData = { ...(vacancy.data || {}) } as Record<string, unknown>;

  if (newStatus === 'Concept' || newStatus === 'Doing' || newStatus === 'Archief') {
    updatedData.publishedToSite = false;
  }

  if (newStatus === 'Gesloten' || newStatus === 'Archief') {
    updatedData.isClosed = true;
  } else {
    updatedData.isClosed = false;
  }

  let dbStatus: Vacancy['status'] = newStatus as Vacancy['status'];
  if (newStatus === 'Doing') {
    updatedData.lifecycleStatus = 'Doing';
    dbStatus = (vacancy.status && String(vacancy.status).toLowerCase() !== 'concept')
      ? vacancy.status
      : 'Intake';
  } else if (updatedData.lifecycleStatus) {
    delete updatedData.lifecycleStatus;
  }

  return {
    updatedData,
    dbStatus,
    updatedVacancy: { ...vacancy, data: updatedData as Vacancy['data'], status: dbStatus },
  };
}

const VacancyList: React.FC<VacancyListProps> = ({ onSelect, user, vacancies, clients = [], candidates = [], isLoading = false, onCreate, onDelete, onUpdate, portalAllowedClientIds }) => {
  const [showNewModal, setShowNewModal] = useState(false);
  const [creationMode, setCreationMode] = useState<'scratch' | 'copy' | 'text'>('scratch');
  const [selectedCopyId, setSelectedCopyId] = useState<string>('');
  const [pastedText, setPastedText] = useState('');

  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';

  // Deletion State
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState<VacancyLifecycleStatus>('Open');
  const [isBulkUpdatingStatus, setIsBulkUpdatingStatus] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Alle');
  const [selectedClient, setSelectedClient] = useState<string>('Alle');
  const [selectedEindklant, setSelectedEindklant] = useState<string>('Alle');
  const [selectedDeadline, setSelectedDeadline] = useState<string>('Alle');

  const [contactPersons, setContactPersons] = useState<{ id: string, name: string, photo_url?: string, company_name?: string, company_logo?: string }[]>([]);
  const [vacancyStages, setVacancyStages] = useState<{ id: string, type: string, vacancy_id: string }[]>([]);
  // Direct application counts per vacancy: { [vacancy_id]: { shortlist, voorgesteld, gesprek, aanbod, plaatsing } }
  const [applicationCounts, setApplicationCounts] = useState<Record<string, { shortlist: number; voorgesteld: number; gesprek: number; aanbod: number; plaatsing: number }>>({});
  const [countsLoading, setCountsLoading] = useState(false);

  const [statusTab, setStatusTab] = useState<VacancyLifecycleStatus>('Open');
  const [sortField, setSortField] = useState<'createdAt' | 'title' | 'client' | 'recruiter' | 'status' | 'vacancy_number' | 'deadline' | 'startDate'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    try {
      const name = sessionStorage.getItem(VACANCIES_CRM_CLIENT_NAME_SESSION_KEY);
      if (name && name.trim()) {
        sessionStorage.removeItem(VACANCIES_CRM_CLIENT_NAME_SESSION_KEY);
        setSelectedClient(name.trim());
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Support Contacts
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [contactSuccess, setContactSuccess] = useState<'whatsapp' | 'schedule' | 'call' | null>(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [eindklantDropdownOpen, setEindklantDropdownOpen] = useState(false);
  const [eindklantSearch, setEindklantSearch] = useState('');

  // Quick Note State
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  const handleSaveQuickNote = async (vacancy: Vacancy, e?: React.MouseEvent | React.FocusEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    if (!editingNoteId) return;

    try {
        const updatedData = { ...vacancy.data, quick_note: editNoteText, quick_note_author: user?.name || 'Onbekend' };
        
        const updatedVacancy = { ...vacancy, data: updatedData };
        if (onUpdate) onUpdate(updatedVacancy);
        await supabase_update(vacancy.id, { data: updatedData });
    } catch (err) {
        console.error("Failed to save quick note", err);
    } finally {
        setEditingNoteId(null);
        setEditNoteText('');
    }
  };

  const handleContactWerfplaats = (type: 'whatsapp' | 'schedule' | 'call') => {
      setShowSupportMenu(false);
      if (type === 'schedule') {
          setShowScheduler(true);
          return;
      }
      if (type === 'whatsapp') {
          window.open('https://wa.me/31624800166?text=Hi%20Thom,%20ik%20heb%20een%20vraag%20over%20Werfplaats.', '_blank');
      }
      setContactSuccess(type);
      setTimeout(() => setContactSuccess(null), 5000);
      // Let menu close after showing success for a while
      setTimeout(() => setShowSupportMenu(false), 2000);
  };

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (clientDropdownOpen && !target.closest('[data-client-dropdown]')) setClientDropdownOpen(false);
      if (eindklantDropdownOpen && !target.closest('[data-eindklant-dropdown]')) setEindklantDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clientDropdownOpen, eindklantDropdownOpen]);

  useEffect(() => {
    const fetchContacts = async () => {
      const contactIds = [...new Set(vacancies.map(v => v.data?.contactPersonId).filter(Boolean))];
      if (contactIds.length === 0) return;
      try {
        const { supabase } = await import('../services/supabaseService');
        const CHUNK_SIZE = 50;
        let allProfiles: any[] = [];
        const promises = [];
        for (let i = 0; i < contactIds.length; i += CHUNK_SIZE) {
            const chunk = contactIds.slice(i, i + CHUNK_SIZE);
            promises.push(supabase.from('profiles').select('id, name, avatar_url, clients(name, logo_url)').in('id', chunk));
        }
        const results = await Promise.all(promises);
        for (const { data } of results) {
            if (data) allProfiles = [...allProfiles, ...data];
        }
        if (allProfiles.length > 0) {
            const mappedProfiles = allProfiles.map(p => ({ 
               ...p, 
               photo_url: p.avatar_url,
               company_name: p.clients?.name
            }));
            setContactPersons(mappedProfiles);
        }
      } catch (e) {
        console.error("Failed to fetch contact persons", e);
      }
    };
    fetchContacts();
  }, [vacancies]);



  // New Vacancy Form
  const [newVacancy, setNewVacancy] = useState({
    title: '',
    clientId: '', // For manual selection
    keywords: '', // Used for booleanString but also for tags in this simple impl
    suggestions: '',
    category: '',
    tags: '', // Comma separated string for input
    deadline: '',
    startDate: '',
    location: '',
    hoursPerWeek: '',
    duration: ''
  });

  const canSelectClient = user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER;

  // Helper function to safely format dates and avoid "Invalid Date"
  const safeFormatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return String(dateString); // Fallback to raw string if invalid
      
      // If it contains a time portion (e.g. T14:30), show the time in 24h format
      if (dateString.includes('T') && !dateString.endsWith('T00:00') && !dateString.endsWith('T00:00:00.000Z')) {
          return date.toLocaleString('nl-NL', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: false
          });
      }
      return date.toLocaleDateString('nl-NL');
    } catch {
      return String(dateString);
    }
  };

  const handleMagicFill = async () => {
    if (!newVacancy.title || isSuggesting) return;
    setIsSuggesting(true);
    try {
      const result = await generateVacancySuggestions(newVacancy.title);
      setNewVacancy(prev => ({
        ...prev,
        keywords: result.keywords || prev.keywords,
        suggestions: result.requirements || prev.suggestions,
        tags: prev.tags || (result.keywords ? result.keywords.split(' ').slice(0, 3).join(', ') : '')
      }));
    } catch (error) {
      console.error("AI Suggestions failed:", error);
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleCreate = async () => {
    if (isCreating) return;

    // Validate based on mode
    if (creationMode === 'scratch' && !newVacancy.title) {
      setError("Vul aub een functietitel in.");
      return;
    }
    if (creationMode === 'copy' && !selectedCopyId) {
      setError("Selecteer een vacature om te kopiëren.");
      return;
    }
    if (creationMode === 'text' && !pastedText) {
      setError("Plak eerst de vacaturetekst.");
      return;
    }

    // Determine Client ID
    // If user CAN select (Admin/Recruiter), they MUST select.
    // If user CANNOT select (Client), use their own ID.
    let effectiveClientId = user?.clientId;

    if (canSelectClient) {
      if (!newVacancy.clientId) {
        setError("Selecteer een klant voor deze vacature.");
        return;
      }
      effectiveClientId = newVacancy.clientId;
    } else {
      // Fallback for safety if somehow user.clientId is missing (should not happen for Client role)
      if (!effectiveClientId) effectiveClientId = '00000000-0000-0000-0000-000000000001';
    }

    setIsCreating(true);
    setError(null);

    try {
      let vacancyData: any;

      if (creationMode === 'scratch') {
        const tagArray = newVacancy.tags.split(',').map(t => t.trim()).filter(t => t.length > 0);
        vacancyData = {
          title: newVacancy.title,
          client_id: effectiveClientId,
          status: 'Concept',
          description: newVacancy.suggestions,
          responsibilities: {},
          data: {
            booleanString: newVacancy.keywords,
            category: newVacancy.category,
            tags: tagArray,
            deadline: newVacancy.deadline,
            startDate: newVacancy.startDate,
            location: newVacancy.location,
            hoursPerWeek: newVacancy.hoursPerWeek,
            duration: newVacancy.duration
          }
        };
      } else if (creationMode === 'text') {
        // Parse text with AI first
        const analysis = await parseVacancyText(pastedText);

        vacancyData = {
          title: analysis.title || 'Nieuwe Vacature',
          client_id: effectiveClientId,
          status: 'Concept',
          description: pastedText, // We store the full text so AI can use it later for research/persona
          responsibilities: {},
          data: {
            category: analysis.category || 'Algemeen',
            booleanString: analysis.keywords,
            tags: analysis.keywords ? analysis.keywords.split(',').map((s: string) => s.trim()) : [],
            marketResearch: '',
            persona: '',
            finalVacancyContent: '' // We leave this empty so user can regenerate if they want, or we could prefill. Leaving empty forces the flow.
          }
        };
      } else {
        // Copy Mode
        const sourceVacancy = vacancies.find(v => v.id === selectedCopyId);
        if (!sourceVacancy) throw new Error("Bronvacature niet gevonden.");

        vacancyData = {
          title: `${sourceVacancy.title} (Copy)`,
          client_id: effectiveClientId, // Copy to current user's client context or selected
          status: 'Concept', // Reset to start
          description: sourceVacancy.description,
          responsibilities: sourceVacancy.responsibilities,
          data: {
            category: sourceVacancy.data?.category,
            tags: sourceVacancy.data?.tags,
            booleanString: sourceVacancy.data?.booleanString, // Keep keywords
            // Explicitly empty AI fields to force new research/persona generation
            marketResearch: '',
            persona: '',
            finalVacancyContent: '',
            structuredTemplates: undefined
          }
        };
      }

      // Insert via Supabase
      const savedVacancy = await supabase_insert_vacancy(vacancyData);

      onSelect({
        ...savedVacancy,
        clientId: savedVacancy.client_id,
        createdAt: new Date(savedVacancy.created_at).toLocaleDateString('nl-NL'),
        data: savedVacancy.data || {}
      }, undefined, true);
      setShowNewModal(false);
      setNewVacancy({
        title: '',
        clientId: '',
        keywords: '',
        suggestions: '',
        category: '',
        tags: '',
        deadline: '',
        startDate: '',
        location: '',
        hoursPerWeek: '',
        duration: ''
      });
      setPastedText('');
      setCreationMode('scratch');
      setSelectedCopyId('');
    } catch (err: any) {
      setError(`Fout bij aanmaken vacature: ${err.message || err} `);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDuplicate = async (e: React.MouseEvent, vacancy: Vacancy) => {
    e.stopPropagation();
    if (isCreating) return; // Reuse create loading state
    setIsCreating(true);

    try {
      const newTitle = `${vacancy.title} (Copy)`;

      const vacancyData = {
        title: newTitle,
        client_id: vacancy.clientId,
        status: 'Concept', // Reset status to start
        description: vacancy.description,
        responsibilities: vacancy.responsibilities,
        data: {
          // Keep meta tags/categories
          category: vacancy.data?.category,
          tags: vacancy.data?.tags,
          // Explicitly RESET AI fields to trigger the "Kickstart" screen in VacancyFlow
          marketResearch: '',
          persona: '',
          finalVacancyContent: '',
          booleanString: vacancy.data?.booleanString // Keep keywords so AI has context
        }
      };

      const savedVacancy = await supabase_insert_vacancy(vacancyData);

      // Auto-open the new copy
      onSelect({
        ...savedVacancy,
        clientId: savedVacancy.client_id,
        createdAt: safeFormatDate(savedVacancy.created_at),
        data: savedVacancy.data || {}
      }, undefined, true);

    } catch (err: any) {
      alert(`Fout bij kopiëren: ${err.message || err} `);
    } finally {
      setIsCreating(false);
    }
  };

  const requestDelete = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeleteConfirmationId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmationId) return;
    setIsDeleting(true);
    try {
      await supabase_delete_vacancy(deleteConfirmationId);
      if (onDelete) onDelete(deleteConfirmationId);
      setDeleteConfirmationId(null);
    } catch (e: any) {
      console.error("Error deleting vacancy:", e);
      alert(`Kon vacature niet verwijderen: ${e.message || e} `);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => supabase_delete_vacancy(id)));
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
      if (onDelete) onDelete(Array.from(selectedIds));
    } catch (e: any) {
      alert(`Fout bij verwijderen: ${e.message || e}`);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const applyVacancyLifecycleStatus = async (vacancy: Vacancy, newStatus: VacancyLifecycleStatus) => {
    const { updatedData, dbStatus, updatedVacancy } = buildVacancyLifecycleUpdate(vacancy, newStatus);
    if (onUpdate) onUpdate(updatedVacancy);
    await supabase_update(vacancy.id, { data: updatedData, status: dbStatus });
  };

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>, vacancy: Vacancy) => {
    e.stopPropagation();
    if (isClientPortal) {
        window.dispatchEvent(new CustomEvent('wt:toast', {
            detail: {
                title: "Geen toegang",
                message: "U kunt de status van de vacature niet wijzigen.",
                type: "error"
            }
        }));
        return;
    }
    const newStatus = e.target.value as VacancyLifecycleStatus;
    try {
        await applyVacancyLifecycleStatus(vacancy, newStatus);
    } catch (err) {
        console.error("Failed to update status", err);
    }
  };

  const handleLiveToggle = async (e: React.MouseEvent, vacancy: Vacancy) => {
    e.stopPropagation();
    if (isClientPortal) {
        window.dispatchEvent(new CustomEvent('wt:toast', {
            detail: {
                title: "Geen toegang",
                message: "Alleen de recruiter kan deze vacature offline halen.",
                type: "error"
            }
        }));
        return;
    }
    const newLiveState = !(vacancy.data?.publishedToSite === true);

    try {
        const updatedData = { ...vacancy.data, publishedToSite: newLiveState };
        
        // DO NOT FORCE STATUS OR ISCLOSED WHEN TOGGLING "PUT LIVE"

        const updatedVacancy = { ...vacancy, data: updatedData };
        if (onUpdate) onUpdate(updatedVacancy); // Immediate optimistic update
        await supabase_update(vacancy.id, { data: updatedData });
    } catch (err) {
        console.error("Failed to toggle live state", err);
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedVacancies.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedVacancies.map(v => v.id)));
    }
  };

  const getOpdrachtgeverName = (v: any) => {
    const rawOpdrachtgever = v.opdrachtgever;
    const unwrappedOpdrachtgever = Array.isArray(rawOpdrachtgever) ? rawOpdrachtgever[0] : rawOpdrachtgever;
    const opdrachtgever = unwrappedOpdrachtgever || clients?.find(c => c.id === (v.opdrachtgever_id || v.data?.opdrachtgever_id)) || MOCK_CLIENTS.find(c => c.id === (v.opdrachtgever_id || v.data?.opdrachtgever_id));
    return opdrachtgever?.name || v.client?.name || v.data?.clientName || '';
  };

  /** Klantportaal: kolom «klant» = eindklant (zelfde scope als vacaturelijst); anders opdrachtgever. */
  const getVacancyKlantColumnName = (v: Vacancy): string => {
    if (!isClientPortal) return getOpdrachtgeverName(v);
    const rawE = (v as any).eindklant;
    const unwrapped = Array.isArray(rawE) ? rawE[0] : rawE;
    const e =
      unwrapped ||
      clients?.find(c => c.id === ((v as any).eindklant_id || (v.data as any)?.eindklant_id)) ||
      MOCK_CLIENTS.find(c => c.id === ((v as any).eindklant_id || (v.data as any)?.eindklant_id));
    const n = String(e?.name || '').trim();
    return n || getOpdrachtgeverName(v);
  };

  const uniqueVacancies = React.useMemo(() => {
    const seenIds = new Set<string>();
    const seenBullhornIds = new Set<string>();
    let base = vacancies.filter(v => {
      if (seenIds.has(v.id)) return false;
      const bhId = (v as any).bullhorn_id || (v.data as any)?.bullhorn_id;
      if (bhId) {
         if (seenBullhornIds.has(bhId)) return false;
         seenBullhornIds.add(bhId);
      }
      seenIds.add(v.id);
      return true;
    });
    // Geen tweede org-filter in portaal: `vacancies` komt uit App `liveVacancies` =
    // supabase_get_vacancies(..., portalLinksOnly). Dubbele filter op platte FK-kolommen
    // verwijderde alle rijen als de light payload alleen geneste `eindklant`/`opdrachtgever` had.
    return base;
  }, [vacancies, isClientPortal]);

  const handleBulkStatusChange = async () => {
    if (selectedIds.size === 0 || isClientPortal) return;
    setIsBulkUpdatingStatus(true);
    const ids = Array.from(selectedIds);
    const toUpdate = uniqueVacancies.filter(v => ids.includes(v.id));
    try {
      await Promise.all(toUpdate.map(v => applyVacancyLifecycleStatus(v, bulkStatusValue)));
      setSelectedIds(new Set());
      setShowBulkStatusModal(false);
      window.dispatchEvent(new CustomEvent('wt:toast', {
        detail: {
          title: 'Status bijgewerkt',
          message: `${toUpdate.length} vacature${toUpdate.length === 1 ? '' : 's'} op ${bulkStatusValue} gezet.`,
          type: 'success',
        },
      }));
    } catch (e: any) {
      alert(`Fout bij status wijzigen: ${e.message || e}`);
    } finally {
      setIsBulkUpdatingStatus(false);
    }
  };

  const activeTabVacancies = React.useMemo(() => {
      return uniqueVacancies.filter(v => {
          if (searchTerm.trim().length > 0) return true; // Show all categories/clients when searching
          if (isClientPortal) return portalLifecycleMatchesVacancyTab(v, statusTab);
          return getVacancyLifecycleStatus(v) === statusTab;
      });
  }, [uniqueVacancies, statusTab, searchTerm, isClientPortal]);

  const categories = Array.from(new Set(activeTabVacancies.map(v => v.data?.category).filter(Boolean)));
  const uniqueClients = Array.from(new Set(activeTabVacancies.map(v => getVacancyKlantColumnName(v)).filter(Boolean))).sort();
  const uniqueEindklanten = Array.from(new Set(activeTabVacancies.map(v => {
      const rawEindklant = (v as any).eindklant;
      const unwrapped = Array.isArray(rawEindklant) ? rawEindklant[0] : rawEindklant;
      const eind = unwrapped || clients?.find(c => c.id === ((v as any).eindklant_id || (v.data as any)?.eindklant_id)) || MOCK_CLIENTS.find(c => c.id === ((v as any).eindklant_id || (v.data as any)?.eindklant_id));
      return eind?.name || '';
  }).filter(Boolean))).sort();

  const isSearching = searchTerm.trim().length > 0;

  const filteredVacancies = uniqueVacancies.filter(v => {
    if (isClientPortal && isPortalArchivedVacancy(v)) return false;
    const clientName = getVacancyKlantColumnName(v);
    const vacNum = String(v.vacancy_number || '');
    const bhId = String((v as any).bullhorn_id || '');
    const eindklantName = (v as any).eindklant?.name || '';

    const titleSafe = String(v.title ?? '');
    const matchesSearch = isSearching ? (
      searchTextIncludes(titleSafe, searchTerm) ||
      searchTextIncludes(clientName, searchTerm) ||
      searchTextIncludes(eindklantName, searchTerm) ||
      vacNum.includes(searchTerm.trim()) ||
      bhId.includes(searchTerm.trim())
    ) : (
      searchTextIncludes(titleSafe, searchTerm) ||
      searchTextIncludes(clientName, searchTerm)
    );

    const matchesCategory = selectedCategory === 'Alle' || v.data?.category === selectedCategory;
    const matchesClient = selectedClient === 'Alle' || clientName === selectedClient;
    const matchesEindklant = selectedEindklant === 'Alle' || eindklantName === selectedEindklant;

    let currentLifecycleStatus = getVacancyLifecycleStatus(v);

    let matchesTab = true;
    if (!isSearching) {
        matchesTab = isClientPortal
            ? portalLifecycleMatchesVacancyTab(v, statusTab)
            : currentLifecycleStatus === statusTab;
    }

    let matchesDeadline = true;
    if (selectedDeadline !== 'Alle') {
        const deadlineStr = v.data?.deadline || v.data?.closingDate || v.data?.planningDate;
        if (!deadlineStr) {
            matchesDeadline = selectedDeadline === 'Geen';
        } else {
            const dDate = new Date(deadlineStr);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            if (selectedDeadline === 'Geen') {
                matchesDeadline = false;
            } else if (selectedDeadline === 'Verlopen') {
                matchesDeadline = dDate < today;
            } else if (selectedDeadline === 'Vandaag') {
                const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
                matchesDeadline = dDate >= today && dDate < tomorrow;
            } else if (selectedDeadline === 'Deze Week') {
                const endOfWeek = new Date(today);
                endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
                matchesDeadline = dDate >= today && dDate <= endOfWeek;
            } else if (selectedDeadline === 'Komende 14 Dagen') {
                const in14 = new Date(today); in14.setDate(today.getDate() + 14);
                matchesDeadline = dDate >= today && dDate <= in14;
            } else if (selectedDeadline === 'Volgende Week') {
                const nextWeekStart = new Date(today);
                nextWeekStart.setDate(today.getDate() + (7 - today.getDay()) + 1);
                const nextWeekEnd = new Date(nextWeekStart);
                nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
                matchesDeadline = dDate >= nextWeekStart && dDate <= nextWeekEnd;
            } else if (selectedDeadline === 'Komende Maand') {
                const in30 = new Date(today); in30.setDate(today.getDate() + 30);
                matchesDeadline = dDate >= today && dDate <= in30;
            }
        }
    }

    return matchesSearch && matchesCategory && matchesClient && matchesEindklant && matchesTab && matchesDeadline;
  }).sort((a, b) => {

    let valA: any = '';
    let valB: any = '';

    if (sortField === 'createdAt') {
      const parseDate = (d: any) => {
          if (!d) return 0;
          if (typeof d === 'string') {
              let clean = d;
              if (clean.includes('+00') && clean.includes(' ')) {
                 clean = clean.split('+')[0].replace(' ', 'T') + 'Z';
              }
              if (clean.includes('-') && clean.split('-')[0].length <= 2) {
                  const p = clean.split('-');
                  return new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0])).getTime();
              }
              return new Date(clean).getTime() || 0;
          }
          return new Date(d).getTime() || 0;
      };
      valA = parseDate((a as any).created_at || a.createdAt);
      valB = parseDate((b as any).created_at || b.createdAt);
    } else if (sortField === 'vacancy_number') {
      valA = a.vacancy_number || 0;
      valB = b.vacancy_number || 0;
    } else if (sortField === 'title') {
      valA = a.title.toLowerCase();
      valB = b.title.toLowerCase();
    } else if (sortField === 'client') {
      valA = getVacancyKlantColumnName(a).toLowerCase();
      valB = getVacancyKlantColumnName(b).toLowerCase();
    } else if (sortField === 'status') {
      valA = a.status;
      valB = b.status;
    } else if (sortField === 'recruiter') {
      valA = contactPersons.find(p => p.id === a.data?.contactPersonId)?.name?.toLowerCase() || '';
      valB = contactPersons.find(p => p.id === b.data?.contactPersonId)?.name?.toLowerCase() || '';
    } else if (sortField === 'deadline') {
      valA = a.data?.deadline || a.data?.closingDate || a.data?.planningDate || '';
      valB = b.data?.deadline || b.data?.closingDate || b.data?.planningDate || '';
    } else if (sortField === 'startDate') {
      valA = a.data?.startDate || '';
      valB = b.data?.startDate || '';
    }

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filteredVacancies.length / itemsPerPage);
  const paginatedVacancies = useMemo(() => {
     return filteredVacancies.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredVacancies, currentPage, itemsPerPage]);

  const paginatedVacancyIdsStr = useMemo(() => {
     return paginatedVacancies.map(v => v.id).sort().join(',');
  }, [paginatedVacancies]);

  const filteredVacancyIdsStr = useMemo(() => {
     return filteredVacancies.map(v => v.id).sort().join(',');
  }, [filteredVacancies]);

  useEffect(() => {
    const fetchStages = async () => {
      const rawVacancyIds = paginatedVacancies.map(v => v.id).filter(Boolean) as string[];
      const vacancyIds = rawVacancyIds.filter(id => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id));
      if (vacancyIds.length === 0) return;
      try {
        const { supabase } = await import('../services/supabaseService');
        // Split into chunks of 50 to avoid 400 Bad Request from URI length limits
        const CHUNK_SIZE = 50;
        let allStages: any[] = [];
        
        for (let i = 0; i < vacancyIds.length; i += CHUNK_SIZE) {
            const chunk = vacancyIds.slice(i, i + CHUNK_SIZE);
            const { data } = await supabase.from('vacancy_stages').select('id, type, vacancy_id').in('vacancy_id', chunk);
            if (data) allStages = [...allStages, ...data];
        }
        
        if (allStages.length > 0) setVacancyStages(prev => {
            const newArray = [...prev];
            allStages.forEach(stage => {
                if (!newArray.some(s => s.id === stage.id)) newArray.push(stage);
            });
            return newArray;
        });
      } catch (e) {
        console.error("Failed to fetch vacancy stages", e);
      }
    };
    fetchStages();
  }, [paginatedVacancyIdsStr]); // Depend on ID string to prevent infinite loop

  useEffect(() => {
    const fetchAppCounts = async () => {
      // Fetch only for paginated vacancies to prevent 100+ simultaneous queries crashing the browser
      const vacancyIds = paginatedVacancies.map(v => v.id).filter(id => /^[0-9a-fA-F-]{36}$/.test(id)) as string[];
      if (vacancyIds.length === 0) { setCountsLoading(false); return; }
      setCountsLoading(true);
      try {
        const { supabase } = await import('../services/supabaseService');
        const CHUNK = 50;
        let allApps: any[] = [];
        const promises = [];
        for (let i = 0; i < vacancyIds.length; i += CHUNK) {
          const chunk = vacancyIds.slice(i, i + CHUNK);
          promises.push(supabase
            .from('applications')
            .select('vacancy_id, status')
            .in('vacancy_id', chunk)
            .not('status', 'eq', 'Rejected'));
        }
        const results = await Promise.all(promises);
        for (const { data } of results) {
          if (data) allApps = [...allApps, ...data];
        }
        // Build count map
        const map: Record<string, { shortlist: number; voorgesteld: number; gesprek: number; aanbod: number; plaatsing: number }> = {};
        for (const app of allApps) {
          const vid = app.vacancy_id;
          if (!map[vid]) map[vid] = { shortlist: 0, voorgesteld: 0, gesprek: 0, aanbod: 0, plaatsing: 0 };
          const s = (app.status || '').toLowerCase();
          if (s.includes('shortlist') || s === 'new' || s === 'nieuw' || s === 'screening') map[vid].shortlist++;
          else if (s.includes('voorgesteld') || s.includes('proposed') || s.includes('submit')) map[vid].voorgesteld++;
          else if (s.includes('gesprek') || s.includes('interview') || s.includes('i-view')) map[vid].gesprek++;
          else if (s.includes('aanbod') || s.includes('offer')) map[vid].aanbod++;
          else if (s.includes('plaatsing') || s.includes('placed') || s.includes('hired')) map[vid].plaatsing++;
          else map[vid].shortlist++; // fallback: count as shortlist
        }
        setApplicationCounts(map); // Replace the whole map to avoid stale data from other filters
      } catch (e) {
        console.error('Failed to fetch application counts:', e);
      } finally {
        setCountsLoading(false);
      }
    };
    fetchAppCounts();
  }, [paginatedVacancyIdsStr]); // Depend on ID string to prevent infinite loop

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedClient, selectedEindklant, selectedDeadline, statusTab, sortField, sortDirection, vacancies.length]);

  const handleSort = (field: 'createdAt' | 'title' | 'client' | 'recruiter' | 'status' | 'vacancy_number' | 'deadline' | 'startDate') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return <span className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity ml-1">↕</span>;
    return <span className="text-[#e51b23] ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleDirectCreate = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // Determine Client ID logic
      let effectiveClientId = user?.clientId;
      if (canSelectClient) {
        // Fallback for admin if no client context
        if (!effectiveClientId) effectiveClientId = '00000000-0000-0000-0000-000000000001';
      }

      if (!effectiveClientId) {
        throw new Error("Geen Client ID gevonden. Kan geen vacature starten.");
      }

      // Immediate creation of blank draft
      const vacancyData = {
        title: 'Nieuwe Vacature',
        client_id: effectiveClientId,
        status: 'Concept',
        description: '',
        responsibilities: {},
        data: {
          category: 'Algemeen',
          booleanString: '',
          tags: [],
          marketResearch: '',
          persona: '',
          finalVacancyContent: ''
        }
      };

      const savedVacancy = await supabase_insert_vacancy(vacancyData);

      console.log("Vacancy created:", savedVacancy);

      onSelect({
        ...savedVacancy,
        clientId: savedVacancy.client_id,
        createdAt: new Date(savedVacancy.created_at).toLocaleDateString('nl-NL'),
        data: savedVacancy.data || {}
      }, undefined, true);
    } catch (e: any) {
      console.error("Direct create failed:", e);
      setError(e.message || "Kon geen nieuwe vacature starten.");
      alert(`Fout bij starten: ${e.message || "Onbekende fout"} `); // Visible feedback
    } finally {
      setIsCreating(false);
    }
  };

  const handleQuickVacancyCreate = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // Determine Client ID logic
      let effectiveClientId = user?.clientId;
      if (canSelectClient) {
        if (!effectiveClientId) effectiveClientId = '00000000-0000-0000-0000-000000000001';
      }

      if (!effectiveClientId) {
        throw new Error("Geen Client ID gevonden. Kan geen vacature starten.");
      }

      // Immediate creation of blank draft jumping to COMM_TEMPLATES (Builder Step)
      const vacancyData = {
        title: 'Nieuwe Vacature',
        client_id: effectiveClientId,
        status: 'Concept',
        description: '',
        responsibilities: {},
        data: {
          category: 'Algemeen',
          booleanString: '',
          tags: [],
          marketResearch: '',
          persona: '',
          finalVacancyContent: ''
        }
      };

      const savedVacancy = await supabase_insert_vacancy(vacancyData);

      onSelect({
        ...savedVacancy,
        clientId: savedVacancy.client_id,
        createdAt: new Date(savedVacancy.created_at).toLocaleDateString('nl-NL'),
        data: savedVacancy.data || {}
      }, undefined, true);
    } catch (e: any) {
      console.error("Direct create failed:", e);
      setError(e.message || "Kon geen nieuwe vacature starten.");
      alert(`Fout bij starten: ${e.message || "Onbekende fout"} `);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-white">

      {/* ── Top header bar ── */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-[15px] font-bold text-slate-900 leading-none tracking-tight">Vacatures</h1>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">{filteredVacancies.length} {statusTab.toLowerCase()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isClientPortal && (
            <button
              onClick={handleDirectCreate}
              disabled={isCreating}
              className="flex items-center gap-1.5 bg-[#E21E26] hover:bg-[#c1151d] text-white px-4 py-2 rounded-lg shadow-sm transition-all font-semibold text-[12px] tracking-wide active:scale-95 disabled:opacity-70"
            >
              {isCreating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Nieuwe Vacature
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs + filters row ── */}
      <div className="bg-white border-b border-slate-100 px-6 flex items-center justify-between shrink-0">
        {/* Tabs */}
        <div className="flex items-center gap-0">
          { (isClientPortal ? PORTAL_VACANCY_LIFECYCLE_TABS : VACANCY_LIFECYCLE_TABS).map(tab => (
            <button
              key={tab}
              onClick={() => setStatusTab(tab as any)}
              className={`py-3 px-4 text-[12px] font-semibold border-b-2 transition-all ${
                isSearching
                  ? 'border-transparent text-slate-300 cursor-default'
                  : statusTab === tab
                    ? 'border-[#E21E26] text-slate-900'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 py-2">

          {/* Search — wider */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" size={12} />
            <input
              type="text"
              placeholder="Zoek op functie, klant, nummer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-72 pl-7 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-800 outline-none focus:ring-1 focus:ring-[#e51b23] focus:border-[#e51b23] transition-all placeholder:text-slate-300 font-medium"
            />
          </div>

          {/* Category — compact */}
          <div className="relative">
            <Filter className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={10} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-6 pr-5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-medium text-slate-500 outline-none cursor-pointer hover:bg-slate-100 appearance-none transition-all min-w-[100px]"
            >
              <option value="Alle">Categorie</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>

          {/* Clients — searchable custom dropdown */}
          <div className="relative" data-client-dropdown>
            <button
              onClick={() => { setClientDropdownOpen(o => !o); setClientSearch(''); }}
              className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 border rounded-lg text-[11px] font-medium transition-all cursor-pointer min-w-[110px] ${
                selectedClient !== 'Alle'
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                  : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Building2 size={10} className={selectedClient !== 'Alle' ? 'text-indigo-400' : 'text-slate-300'} />
              <span className="truncate max-w-[90px]">{selectedClient === 'Alle' ? 'Klant / Opdrachtgever' : selectedClient}</span>
              {selectedClient !== 'Alle' && (
                <span
                  onClick={(e) => { e.stopPropagation(); setSelectedClient('Alle'); setClientDropdownOpen(false); }}
                  className="ml-auto text-indigo-300 hover:text-indigo-600 cursor-pointer text-[10px] font-bold leading-none"
                >✕</span>
              )}
            </button>

            {clientDropdownOpen && (
              <div className="absolute right-0 top-[110%] z-50 w-56 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-fadeIn">
                <div className="p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" size={11} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Zoek klant..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="w-full pl-7 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] outline-none focus:ring-1 focus:ring-indigo-300 placeholder:text-slate-300"
                    />
                  </div>
                </div>
                <div className="max-h-52 overflow-y-auto py-1">
                  <button
                    onClick={() => { setSelectedClient('Alle'); setClientDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] font-medium transition-colors ${
                      selectedClient === 'Alle' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    Alle klanten
                  </button>
                  {uniqueClients
                    .filter(c => !clientSearch || String(c).toLowerCase().includes(clientSearch.toLowerCase()))
                    .map(client => (
                      <button
                        key={String(client)}
                        onClick={() => { setSelectedClient(String(client)); setClientDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-[12px] font-medium transition-colors ${
                          selectedClient === String(client) ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {String(client)}
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Werkplek (Eindklant) — searchable custom dropdown */}
          <div className="relative" data-eindklant-dropdown>
            <button
              onClick={() => { setEindklantDropdownOpen(o => !o); setEindklantSearch(''); }}
              className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 border rounded-lg text-[11px] font-medium transition-all cursor-pointer min-w-[110px] ${
                selectedEindklant !== 'Alle'
                  ? 'bg-rose-50 border-rose-200 text-rose-700'
                  : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
            >
              <MapPin size={10} className={selectedEindklant !== 'Alle' ? 'text-rose-400' : 'text-slate-300'} />
              <span className="truncate max-w-[90px]">{selectedEindklant === 'Alle' ? 'Werkplek / Eindklant' : selectedEindklant}</span>
              {selectedEindklant !== 'Alle' && (
                <span
                  onClick={(e) => { e.stopPropagation(); setSelectedEindklant('Alle'); setEindklantDropdownOpen(false); }}
                  className="ml-auto text-rose-300 hover:text-rose-600 cursor-pointer text-[10px] font-bold leading-none"
                >✕</span>
              )}
            </button>

            {eindklantDropdownOpen && (
              <div className="absolute right-0 top-[110%] z-50 w-56 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-fadeIn">
                <div className="p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" size={11} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Zoek werkplek..."
                      value={eindklantSearch}
                      onChange={(e) => setEindklantSearch(e.target.value)}
                      className="w-full pl-7 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] outline-none focus:ring-1 focus:ring-rose-300 placeholder:text-slate-300"
                    />
                  </div>
                </div>
                <div className="max-h-52 overflow-y-auto py-1">
                  <button
                    onClick={() => { setSelectedEindklant('Alle'); setEindklantDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] font-medium transition-colors ${
                      selectedEindklant === 'Alle' ? 'bg-rose-50 text-rose-700' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    Alle eindklanten
                  </button>
                  {uniqueEindklanten
                    .filter(c => !eindklantSearch || String(c).toLowerCase().includes(eindklantSearch.toLowerCase()))
                    .map(eindklant => (
                      <button
                        key={String(eindklant)}
                        onClick={() => { setSelectedEindklant(String(eindklant)); setEindklantDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-[12px] font-medium transition-colors ${
                          selectedEindklant === String(eindklant) ? 'bg-rose-50 text-rose-700' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {String(eindklant)}
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>


          {/* Deadline */}
          <div className="relative">
            <Calendar className={`absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${selectedDeadline !== 'Alle' ? 'text-orange-400' : 'text-slate-300'}`} size={10} />
            <select
              value={selectedDeadline}
              onChange={(e) => setSelectedDeadline(e.target.value)}
              className={`pl-6 pr-5 py-1.5 border rounded-lg text-[11px] font-medium outline-none cursor-pointer appearance-none transition-all min-w-[110px] ${
                selectedDeadline !== 'Alle' ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
            >
              <option value="Alle">Deadline</option>
              <option value="Vandaag">Vandaag</option>
              <option value="Deze Week">Deze week</option>
              <option value="Komende 14 Dagen">Komende 14 dagen</option>
              <option value="Volgende Week">Volgende week</option>
              <option value="Komende Maand">Komende maand</option>
              <option value="Verlopen">⚠️ Verlopen</option>
              <option value="Geen">Geen deadline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Search mode notice */}
      {isSearching && (
        <div className="flex items-center gap-2 px-6 py-2 bg-amber-50 border-b border-amber-100 text-[11px] font-medium text-amber-700 shrink-0">
          <Search size={11} />
          Zoekresultaten over <span className="font-bold">alle tabs</span> — {filteredVacancies.length} gevonden
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">

      {typeof isLoading === 'number' || isLoading ? (
        typeof isLoading === 'number' ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-sm">
                <div className="flex items-center justify-between mb-2">
                   <p className="text-xs font-black text-slate-700 uppercase tracking-widest">Vacatures Synchroniseren</p>
                   <p className="text-xs font-bold text-[#e51b23]">{isLoading}%</p>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                   <div className="h-full bg-[#e51b23] transition-all duration-300 ease-out" style={{ width: `${isLoading}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 font-medium text-center mt-3 animate-pulse">Bijwerken van historische en actieve vacatures...</p>
            </div>
          </div>
        ) : (
        // Loading Skeleton
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm animate-pulse flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg"></div>
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-slate-100 rounded-md"></div>
                  <div className="h-3 w-20 bg-slate-100 rounded-md"></div>
                </div>
              </div>
              <div className="h-6 w-16 bg-slate-100 rounded-lg"></div>
            </div>
          ))}
        </div>
        )
      ) : paginatedVacancies.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl py-20 flex flex-col items-center justify-center text-center px-4 hover:border-gray-300 transition-colors">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
            <Briefcase size={32} className="text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-1">Geen Vacatures</h3>
          {user?.role !== UserRole.CLIENT && user?.role !== UserRole.CLIENT_RECRUITER ? (
            <>
              <p className="text-slate-500 text-xs font-medium max-w-xs mb-6">Er zijn geen (actieve) vacatures gevonden voor je huidige weergave.</p>
              <button
                onClick={handleDirectCreate}
                disabled={isCreating}
                className="flex items-center gap-2 bg-[#0F172A] text-white px-6 py-2.5 rounded-xl shadow-lg hover:bg-black transition-all font-black text-[10px] uppercase tracking-widest active:scale-95"
              >
                {isCreating ? <Loader2 size={14} className="animate-spin" /> : 'Start Nieuwe Campagne'}
              </button>
            </>
          ) : (
            <p className="text-slate-500 text-xs font-medium max-w-xs mb-6">Er zijn op dit moment geen vacatures zichtbaar in het portaal.</p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-x-auto">
          <table className="hidden md:table w-full text-left border-collapse min-w-[900px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                {!isClientPortal && (
                <th className="py-3 px-4 w-[3%]">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === paginatedVacancies.length && paginatedVacancies.length > 0}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-[#e51b23] focus:ring-[#e51b23] cursor-pointer"
                  />
                </th>
                )}
                <th onClick={() => handleSort('vacancy_number')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap w-[6%]">
                  <div className="flex items-center gap-1"># {renderSortIndicator('vacancy_number')}</div>
                </th>
                <th onClick={() => handleSort('title')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap">
                  <div className="flex items-center gap-1">Functie {renderSortIndicator('title')}</div>
                </th>
                <th onClick={() => handleSort('client')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap w-[14%]">
                  <div className="flex items-center gap-1">{isClientPortal ? 'Eindklant' : 'Klant/Opdrachtgever'} {renderSortIndicator('client')}</div>
                </th>
                <th onClick={() => handleSort('recruiter')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap w-[12%]">
                  <div className="flex items-center gap-1">Recruiter {renderSortIndicator('recruiter')}</div>
                </th>
                <th className="py-3 px-4 text-[10px] font-black uppercase tracking-widest whitespace-nowrap w-[26%]">Pipeline</th>
                <th onClick={() => handleSort('deadline')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap w-[9%]">
                  <div className="flex items-center gap-1">Deadline {renderSortIndicator('deadline')}</div>
                </th>
                <th className="py-3 px-4 text-[10px] font-black uppercase tracking-widest whitespace-nowrap w-[8%]">Put Live</th>
                <th onClick={() => handleSort('status')} className="py-3 px-4 text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap w-[7%]">
                  <div className="flex items-center gap-1">Status {renderSortIndicator('status')}</div>
                </th>
                <th className="py-3 px-4 w-[5%]"></th>
              </tr>
            </thead>
            <tbody>
                {paginatedVacancies.map((vacancy) => {
                  const unwrappedClient = Array.isArray((vacancy as any).client) ? (vacancy as any).client[0] : (vacancy as any).client;
                  const client = unwrappedClient || clients?.find(c => c.id === vacancy.clientId) || MOCK_CLIENTS.find(c => c.id === vacancy.clientId);
                  
                  const rawEindklant = (vacancy as any).eindklant;
                  const unwrappedEindklant = Array.isArray(rawEindklant) ? rawEindklant[0] : rawEindklant;
                  const eindklant = unwrappedEindklant || clients?.find(c => c.id === ((vacancy as any).eindklant_id || (vacancy.data as any)?.eindklant_id)) || MOCK_CLIENTS.find(c => c.id === ((vacancy as any).eindklant_id || (vacancy.data as any)?.eindklant_id));
                  
                  const rawOpdrachtgever = (vacancy as any).opdrachtgever;
                  const unwrappedOpdrachtgever = Array.isArray(rawOpdrachtgever) ? rawOpdrachtgever[0] : rawOpdrachtgever;
                  const opdrachtgever = unwrappedOpdrachtgever || clients?.find(c => c.id === ((vacancy as any).opdrachtgever_id || (vacancy.data as any)?.opdrachtgever_id)) || MOCK_CLIENTS.find(c => c.id === ((vacancy as any).opdrachtgever_id || (vacancy.data as any)?.opdrachtgever_id));
                  
                  const opdrachtgeverOrg = opdrachtgever || client;
                  const werkplekOrg = eindklant;
                  const displayOrgForLogo = werkplekOrg || opdrachtgeverOrg;
                  const displayOrg = isClientPortal ? (werkplekOrg || opdrachtgeverOrg) : opdrachtgeverOrg;
                  
                const locText = (vacancy.data as any)?.location || (vacancy.data as any)?.city || (vacancy.data as any)?.locatie || (vacancy.data as any)?.werkplek || (vacancy.data as any)?.address?.city || (vacancy as any)?.location || (vacancy as any)?.city || werkplekOrg?.address_city || opdrachtgeverOrg?.address_city || client?.address_city || 'Locatie onbekend';
                  
                  const recruiter = contactPersons.find(p => p.id === vacancy.data?.contactPersonId);
                const isLive = vacancy.data?.publishedToSite;
                const dl = vacancy.data?.deadline || vacancy.data?.closingDate || vacancy.data?.planningDate;
                const diffDays = dl ? Math.ceil((new Date(dl).getTime() - new Date().setHours(0,0,0,0)) / 86400000) : null;
                const dlExpired = diffDays !== null && diffDays < 0;
                const dlUrgent = diffDays !== null && diffDays >= 0 && diffDays <= 7;
                const c = applicationCounts[vacancy.id] || { shortlist: 0, voorgesteld: 0, gesprek: 0, aanbod: 0, plaatsing: 0 };
                const totalCandidates = c.shortlist + c.voorgesteld + c.gesprek + c.aanbod + c.plaatsing;

                return (
                  <tr
                    key={vacancy.id}
                    className={`group border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer ${selectedIds.has(vacancy.id) ? 'bg-red-50/40' : ''}`}
                    onClick={() => !selectedIds.has(vacancy.id) && onSelect(vacancy)}
                  >
                    {/* Checkbox */}
                    {!isClientPortal && (
                    <td className="py-3.5 px-4" onClick={(e) => toggleSelect(vacancy.id, e)}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(vacancy.id)}
                        onChange={() => {}}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-[#e51b23] focus:ring-[#e51b23] cursor-pointer"
                      />
                    </td>
                    )}
                    {/* # Number */}
                    <td className="py-3.5 px-4">
                      <span className="text-[11px] font-mono font-semibold text-slate-400">
                        {vacancy.vacancy_number ? `#${vacancy.vacancy_number}` : '—'}
                      </span>
                    </td>

                    {/* Function + org */}
                    <td className="py-3.5 px-4 max-w-[280px]">
                      <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 border border-slate-200/60 shadow-sm overflow-hidden p-[3px]">
                            {displayOrgForLogo?.logo_url ? (
                              <img src={displayOrgForLogo.logo_url} alt={displayOrgForLogo.name || 'Logo'} className="w-full h-full object-contain" />
                            ) : (
                              <Building2 size={14} className="text-slate-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-bold text-slate-800 truncate group-hover:text-[#E21E26] transition-colors leading-tight">{vacancy.title}</p>
                              <div className="flex items-center mt-0.5 gap-2">
                                  {eindklant?.name && (
                                      <div className="flex items-center min-w-0 bg-white border border-slate-100 rounded py-0.5 px-1.5 shadow-sm gap-1.5">
                                            {eindklant.logo_url ? (
                                              <img src={eindklant.logo_url} alt="Logo" className="w-3 h-3 rounded-sm object-cover" />
                                            ) : (
                                              <Building2 size={10} className="text-slate-400" />
                                            )}
                                            <span className="text-[10px] font-bold text-indigo-700/90 truncate">{eindklant.name}</span>
                                        </div>
                                  )}
                              </div>
                              {/* Quick Note */}
                              {!isClientPortal && (
                              <div className="mt-1" onClick={e => e.stopPropagation()}>
                                  {editingNoteId === vacancy.id ? (
                                      <div className="flex items-center gap-1">
                                          <input
                                              autoFocus
                                              value={editNoteText}
                                              onChange={e => setEditNoteText(e.target.value)}
                                              onKeyDown={e => {
                                                  if (e.key === 'Enter') handleSaveQuickNote(vacancy, e);
                                                  if (e.key === 'Escape') setEditingNoteId(null);
                                              }}
                                              onBlur={(e) => handleSaveQuickNote(vacancy, e)}
                                              className="text-[10px] bg-yellow-50 border border-yellow-200 rounded px-1.5 py-0.5 w-full outline-none text-yellow-800 placeholder-yellow-400/50"
                                              placeholder="Typ notitie en druk op Enter..."
                                          />
                                      </div>
                                  ) : (
                                      <div 
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingNoteId(vacancy.id);
                                              setEditNoteText(vacancy.data?.quick_note || '');
                                          }}
                                          className="text-[10px] text-slate-500 flex items-center gap-1 cursor-pointer hover:text-slate-800 transition-colors group/note max-w-full"
                                      >
                                          <FileText size={10} className="text-slate-400 group-hover/note:text-amber-500 shrink-0" />
                                          <span className="truncate">{vacancy.data?.quick_note ? <span className="bg-yellow-50 px-1 py-0.5 rounded text-yellow-800">{vacancy.data.quick_note} {vacancy.data.quick_note_author && <span className="opacity-50 ml-1 text-[9px]">({vacancy.data.quick_note_author})</span>}</span> : <span className="opacity-0 group-hover/note:opacity-100 transition-opacity italic">Voeg snelle notitie toe...</span>}</span>
                                      </div>
                                  )}
                              </div>
                              )}
                          </div>
                      </div>
                    </td>

                    {/* Klant/Opdrachtgever */}
                    <td className="py-3.5 px-4 min-w-[150px]">
                      {displayOrg?.name ? (
                        <div className="flex items-center gap-2.5">
                          {displayOrg.logo_url ? (
                            <img src={displayOrg.logo_url} alt={displayOrg.name} className="w-6 h-6 rounded-lg object-cover border border-slate-100 shrink-0 shadow-sm" />
                          ) : (
                            <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 shadow-sm">
                              <Building2 size={12} className="text-slate-400" />
                            </div>
                          )}
                          <span className="text-[12px] font-semibold text-slate-800 truncate">{displayOrg.name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>

                    {/* Recruiter */}
                    <td className="py-3.5 px-4 min-w-[150px]">
                      {recruiter ? (
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="flex items-center gap-2">
                            {recruiter.photo_url ? (
                                <img src={recruiter.photo_url} alt={recruiter.name} className="w-6 h-6 rounded-full object-cover border border-slate-200 shrink-0" />
                            ) : (
                                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 border border-slate-200">
                                <UserIcon size={11} className="text-indigo-500" />
                                </div>
                            )}
                            <span className="text-[12px] font-bold text-slate-700 truncate">{recruiter.name}</span>
                          </div>
                          {recruiter.company_name && (
                              <div className="flex items-center gap-1.5 pl-8 mt-0.5">
                                  {recruiter.company_logo ? (
                                    <img src={recruiter.company_logo} alt="Company" className="w-3.5 h-3.5 rounded object-cover shrink-0 opacity-80" />
                                  ) : (
                                    <Building2 size={8} className="text-slate-400 shrink-0 opacity-60" />
                                  )}
                                  <span className="text-[10px] font-semibold text-slate-500/80 truncate">{recruiter.company_name}</span>
                              </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>

                    {/* Pipeline stages */}
                    <td className="py-3.5 px-4">
                      {countsLoading && !applicationCounts[vacancy.id] ? (
                        <div className="flex items-center gap-1">
                          {[0,1,2,3,4].map(i => (
                            <div key={i} className="flex flex-col flex-1 items-center justify-center rounded-md py-1 px-0.5 bg-slate-50">
                              <Loader2 size={10} className="animate-spin text-slate-300" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {[
                            { label: 'Shortlist',   value: c.shortlist },
                            { label: 'Voorgesteld', value: c.voorgesteld },
                            { label: 'Gesprek',     value: c.gesprek },
                            { label: 'Aanbod',      value: c.aanbod },
                            { label: 'Plaatsing',   value: c.plaatsing },
                          ].map((s, i) => (
                            <div
                              key={i}
                              title={`${s.label}: ${s.value}`}
                              className={`flex flex-col flex-1 items-center justify-center rounded-md py-1 px-0.5 transition-colors ${
                                s.value > 0 ? 'bg-indigo-50' : 'bg-slate-50'
                              }`}
                            >
                              <span className={`text-[12px] font-bold leading-none ${s.value > 0 ? 'text-indigo-600' : 'text-slate-300'}`}>{s.value}</span>
                              <span className={`text-[7px] font-medium uppercase tracking-wide leading-none mt-0.5 ${s.value > 0 ? 'text-indigo-300' : 'text-slate-200'}`}>{s.label}</span>
                            </div>
                          ))}
                          {totalCandidates > 0 && (
                            <span className="ml-1.5 text-[10px] font-bold text-slate-400 shrink-0">{totalCandidates}</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Deadline */}
                    <td className="py-3.5 px-4">
                      {dl ? (
                        <span className={`text-[12px] font-medium whitespace-nowrap ${dlExpired ? 'text-red-500' : dlUrgent ? 'text-orange-500' : 'text-slate-500'}`}>
                          {safeFormatDate(dl)}
                          {dlExpired && <span className="ml-1 text-[9px] font-bold uppercase bg-red-100 text-red-500 px-1 py-0.5 rounded">verlopen</span>}
                          {dlUrgent && !dlExpired && <span className="ml-1 text-[9px] font-bold bg-orange-100 text-orange-500 px-1 py-0.5 rounded">{diffDays}d</span>}
                        </span>
                      ) : (
                        <span className="text-slate-200 text-xs">—</span>
                      )}
                    </td>

                    {/* Put Live */}
                    <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                        <div 
                            className={`px-2.5 py-1 rounded-full flex items-center justify-center gap-1.5 w-fit border shadow-sm cursor-pointer hover:scale-105 transition-all ${isLive ? 'bg-emerald-50 border-emerald-100 hover:bg-emerald-100' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                            onClick={(e) => handleLiveToggle(e, vacancy)}
                            title={isLive ? 'Zet offline' : 'Zet live op portal'}
                        >
                             <div className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-emerald-500 shadow-[0_0_4px_rgba(16,185,129,0.5)]' : 'bg-slate-300'}`}></div>
                             <span className={`text-[9px] font-bold uppercase tracking-wider ${isLive ? 'text-emerald-700' : 'text-slate-500'}`}>{isLive ? 'Live' : 'Uit'}</span>
                        </div>
                    </td>

                    {/* Status — klantportaal: alleen tonen, geen wijziging */}
                    <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const ls = getVacancyLifecycleStatus(vacancy);
                          const statusClass = `px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                            ls === 'Open' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                            ls === 'Doing' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' :
                            ls === 'Gesloten' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                            ls === 'Archief' ? 'bg-slate-100 text-slate-500 border-slate-200' :
                            'bg-slate-50 text-slate-400 border-slate-200'
                          }`;
                          if (isClientPortal) {
                            const label = ls === 'Concept' ? 'CONCEPT' : ls === 'Doing' ? 'DOING' : ls === 'Open' ? 'OPEN' : ls === 'Gesloten' ? 'GESLOTEN' : ls === 'Archief' ? 'ARCHIEF' : String(ls).toUpperCase();
                            return (
                              <span className={statusClass} title="Status wijzigt alleen via uw recruiter.">
                                {label}
                              </span>
                            );
                          }
                          return (
                            <select
                              value={ls}
                              onChange={(e) => handleStatusChange(e, vacancy)}
                              className={`${statusClass} appearance-none cursor-pointer outline-none`}
                              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundPosition: 'right 0.2rem center', backgroundRepeat: 'no-repeat', backgroundSize: '0.8em 0.8em', paddingRight: '1.2rem' }}
                            >
                              <option value="Concept">CONCEPT</option>
                              <option value="Doing">DOING</option>
                              <option value="Open">OPEN</option>
                              <option value="Gesloten">GESLOTEN</option>
                              <option value="Archief">ARCHIEF</option>
                            </select>
                          );
                        })()}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => onSelect(vacancy)}
                          className="p-1.5 text-slate-400 hover:text-[#E21E26] hover:bg-red-50 rounded-lg transition-all"
                          title="Bekijk Details"
                        >
                          <Eye size={14} />
                        </button>
                        {!isClientPortal && (
                          <>
                            <button
                              onClick={(e) => handleDuplicate(e, vacancy)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                              title="Dupliceren"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={(e) => requestDelete(vacancy.id, e)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title="Verwijderen"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* MOBILE CARDS VIEW */}
          <div className="md:hidden flex flex-col gap-3 p-3">
             {paginatedVacancies.map(vacancy => {
                  const unwrappedClient = Array.isArray((vacancy as any).client) ? (vacancy as any).client[0] : (vacancy as any).client;
                  const client = unwrappedClient || clients?.find(c => c.id === vacancy.clientId) || MOCK_CLIENTS.find(c => c.id === vacancy.clientId);

                  const rawEindklant = (vacancy as any).eindklant;
                  const unwrappedEindklant = Array.isArray(rawEindklant) ? rawEindklant[0] : rawEindklant;
                  const eindklant = unwrappedEindklant || clients?.find(c => c.id === ((vacancy as any).eindklant_id || (vacancy.data as any)?.eindklant_id)) || MOCK_CLIENTS.find(c => c.id === ((vacancy as any).eindklant_id || (vacancy.data as any)?.eindklant_id));

                  const rawOpdrachtgever = (vacancy as any).opdrachtgever;
                  const unwrappedOpdrachtgever = Array.isArray(rawOpdrachtgever) ? rawOpdrachtgever[0] : rawOpdrachtgever;
                  const opdrachtgever = unwrappedOpdrachtgever || clients?.find(c => c.id === ((vacancy as any).opdrachtgever_id || (vacancy.data as any)?.opdrachtgever_id)) || MOCK_CLIENTS.find(c => c.id === ((vacancy as any).opdrachtgever_id || (vacancy.data as any)?.opdrachtgever_id));
                const displayOrg = eindklant || opdrachtgever || client;
                
                 const c = applicationCounts[vacancy.id] || { shortlist: 0, voorgesteld: 0, gesprek: 0, aanbod: 0, plaatsing: 0 };
                 const activeCount = c.shortlist + c.voorgesteld + c.gesprek + c.aanbod;
                 const hiredCount = c.plaatsing;

                const locText = (vacancy.data as any)?.location || (vacancy.data as any)?.city || (vacancy.data as any)?.locatie || (vacancy.data as any)?.werkplek || (vacancy.data as any)?.address?.city || (vacancy.data as any)?.location || (vacancy.data as any)?.city || eindklant?.address_city || opdrachtgever?.address_city || client?.address_city || 'Locatie onbekend';

                return (
                   <div key={vacancy.id} onClick={() => onSelect(vacancy)} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 active:scale-[0.98] transition-all flex flex-col gap-3 select-none">
                      <div className="flex justify-between items-start">
                         <div className="flex items-center gap-3 pr-2">
                            {displayOrg?.logo_url ? <img src={displayOrg.logo_url} className="w-10 h-10 rounded-full border border-slate-100 object-cover shrink-0" /> : <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100 shrink-0"><Building2 size={16} className="text-slate-400" /></div>}
                             <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-black text-slate-900 leading-tight line-clamp-2">{vacancy.title}</h3>
                                <p className="text-[10px] font-bold text-slate-500 mt-0.5">{eindklant?.name || opdrachtgever?.name || client?.name || 'Onbekend'}</p>
                                {/* Quick Note */}
                                {!isClientPortal && (
                                <div className="mt-1.5" onClick={e => e.stopPropagation()}>
                                    {editingNoteId === vacancy.id ? (
                                        <div className="flex items-center gap-1">
                                            <input
                                                autoFocus
                                                value={editNoteText}
                                                onChange={e => setEditNoteText(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleSaveQuickNote(vacancy, e);
                                                    if (e.key === 'Escape') setEditingNoteId(null);
                                                }}
                                                onBlur={(e) => handleSaveQuickNote(vacancy, e)}
                                                className="text-[11px] bg-yellow-50 border border-yellow-200 rounded px-2 py-1 w-full outline-none text-yellow-800 placeholder-yellow-400/50"
                                                placeholder="Typ notitie en druk op Enter..."
                                            />
                                        </div>
                                    ) : (
                                        <div 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingNoteId(vacancy.id);
                                                setEditNoteText(vacancy.data?.quick_note || '');
                                            }}
                                            className="text-[11px] text-slate-500 flex items-center gap-1.5 cursor-pointer hover:text-slate-800 transition-colors group/note max-w-full"
                                        >
                                            <FileText size={11} className="text-slate-400 group-hover/note:text-amber-500 shrink-0" />
                                            <span className="truncate">{vacancy.data?.quick_note ? <span className="bg-yellow-50 px-1.5 py-0.5 rounded text-yellow-800">{vacancy.data.quick_note} {vacancy.data.quick_note_author && <span className="opacity-50 ml-1 text-[9px]">({vacancy.data.quick_note_author})</span>}</span> : <span className="opacity-50 italic">Voeg snelle notitie toe...</span>}</span>
                                        </div>
                                    )}
                                </div>
                                )}
                             </div>
                         </div>
                         <span className={`shrink-0 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${vacancy.data?.publishedToSite ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                            {vacancy.data?.publishedToSite ? 'Live' : 'Concept'}
                         </span>
                      </div>
                      
                      <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded-xl border border-slate-100 mt-1">
                         <div className="flex items-center gap-2">
                            <div className="flex -space-x-2">
                               <div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[9px] font-black text-slate-600 z-10" title="Actief in proces">{activeCount}</div>
                               <div className="w-7 h-7 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center text-[9px] font-black text-emerald-600" title="Aangenomen">{hiredCount}</div>
                            </div>
                            <span className="text-[10px] font-bold text-slate-500">Pipeline</span>
                         </div>
                         <div className="flex flex-col items-end text-[10px] font-bold text-slate-400">
                            <span className="flex items-center gap-1"><Calendar size={10} className="text-orange-400" /> {safeFormatDate(vacancy.data?.deadline || vacancy.data?.closingDate || vacancy.data?.planningDate)}</span>
                         </div>
                      </div>
                   </div>
                );
             })}
          </div>
          </div>
          
          {totalPages > 0 && (
            <div className="border-t border-slate-100 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between bg-white rounded-b-xl gap-3">
              <span className="text-[11px] font-medium text-slate-400 flex items-center flex-wrap gap-2">
                <span>{filteredVacancies.length} vacatures</span>
                <span>·</span>
                {(() => {
                  const totals = filteredVacancies.reduce((acc, v) => {
                    const c = applicationCounts[v.id] || { shortlist: 0, voorgesteld: 0, gesprek: 0, aanbod: 0, plaatsing: 0 };
                    acc.shortlist += c.shortlist;
                    acc.voorgesteld += c.voorgesteld;
                    acc.gesprek += c.gesprek;
                    acc.aanbod += c.aanbod;
                    acc.plaatsing += c.plaatsing;
                    return acc;
                  }, { shortlist: 0, voorgesteld: 0, gesprek: 0, aanbod: 0, plaatsing: 0 });
                  
                  return (
                    <span className="flex items-center gap-1.5">
                      <span className="flex items-center gap-1"><span className="font-bold text-indigo-500">{totals.shortlist}</span> shortlist</span>
                      <span className="text-slate-300">·</span>
                      <span className="flex items-center gap-1"><span className="font-bold text-indigo-500">{totals.voorgesteld}</span> voorgesteld</span>
                      <span className="text-slate-300">·</span>
                      <span className="flex items-center gap-1"><span className="font-bold text-indigo-500">{totals.gesprek}</span> gesprek</span>
                      <span className="text-slate-300">·</span>
                      <span className="flex items-center gap-1"><span className="font-bold text-indigo-500">{totals.aanbod}</span> aanbod</span>
                      <span className="text-slate-300">·</span>
                      <span className="flex items-center gap-1"><span className="font-bold text-emerald-500">{totals.plaatsing}</span> plaatsing</span>
                    </span>
                  );
                })()}
                <span className="hidden md:inline">·</span>
                <span>pagina {currentPage} van {totalPages}</span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-[11px] font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
                >
                  Vorige
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = currentPage - 2 + i;
                    if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-7 h-7 flex items-center justify-center text-[11px] font-semibold rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-[#E21E26] text-white'
                              : 'text-slate-500 hover:bg-slate-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-[11px] font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
                >
                  Volgende
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FLOATING SELECTION BAR */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[250] animate-fadeIn">
          <div className="flex items-center gap-3 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-slate-700">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#e51b23] rounded-md flex items-center justify-center text-[10px] font-black">
                {selectedIds.size}
              </div>
              <span className="text-[12px] font-bold text-slate-200">
                {selectedIds.size === 1 ? 'vacature geselecteerd' : 'vacatures geselecteerd'}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-700" />
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] font-semibold text-slate-400 hover:text-white transition-colors"
            >
              Deselecteer
            </button>
            {!isClientPortal && (
              <button
                onClick={() => setShowBulkStatusModal(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[11px] font-black uppercase tracking-widest transition-all active:scale-95"
              >
                <Tag size={13} /> Status wijzigen
              </button>
            )}
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[11px] font-black uppercase tracking-widest transition-all active:scale-95"
            >
              <Trash2 size={13} /> Verwijder {selectedIds.size}
            </button>
          </div>
        </div>
      )}

      {/* BULK STATUS MODAL */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn" onClick={() => !isBulkUpdatingStatus && setShowBulkStatusModal(false)}>
          <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-scaleIn" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4 text-indigo-600">
                <Tag size={28} />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-2">Status wijzigen</h3>
              <p className="text-sm text-slate-500 font-medium mb-6">
                Zet <strong>{selectedIds.size}</strong> geselecteerde vacature{selectedIds.size === 1 ? '' : 's'} op één status.
              </p>
              <select
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value as VacancyLifecycleStatus)}
                disabled={isBulkUpdatingStatus}
                className="w-full mb-8 px-4 py-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-800 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="Concept">Concept</option>
                <option value="Doing">Doing</option>
                <option value="Open">Open</option>
                <option value="Gesloten">Gesloten</option>
                <option value="Archief">Archief</option>
              </select>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowBulkStatusModal(false)}
                  disabled={isBulkUpdatingStatus}
                  className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleBulkStatusChange}
                  disabled={isBulkUpdatingStatus}
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isBulkUpdatingStatus ? <Loader2 className="animate-spin" size={16} /> : 'Toepassen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BULK DELETE CONFIRMATION MODAL */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-scaleIn" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 text-red-500">
                <Trash2 size={28} />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-2">{selectedIds.size} Vacatures Verwijderen?</h3>
              <p className="text-sm text-slate-500 font-medium mb-8">
                Je staat op het punt <strong>{selectedIds.size} vacatures</strong> permanent te verwijderen. Dit heeft ook invloed op gekoppelde sollicitaties.
              </p>
              <div className="flex gap-3 w-full">
                <button onClick={() => setShowBulkDeleteModal(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors">
                  Annuleren
                </button>
                <button onClick={handleBulkDelete} disabled={isBulkDeleting} className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors flex items-center justify-center gap-2">
                  {isBulkDeleting ? <Loader2 className="animate-spin" size={16} /> : `Verwijder ${selectedIds.size}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SINGLE DELETE CONFIRMATION MODAL */}
      {
        deleteConfirmationId && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/50 backdrop-blur-sm animate-fadeIn" onClick={() => setDeleteConfirmationId(null)}>
            <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-scaleIn" onClick={e => e.stopPropagation()}>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 text-red-500">
                  <Trash2 size={32} />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-2">Vacature Verwijderen?</h3>
                <p className="text-sm text-slate-500 font-medium mb-8">
                  Weet je zeker dat je deze vacature wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setDeleteConfirmationId(null)}
                    className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={isDeleting}
                    className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {isDeleting ? <Loader2 className="animate-spin" size={16} /> : 'Verwijderen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* New Vacancy Modal */}
      {
        showNewModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
            <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl overflow-hidden animate-scaleIn relative flex flex-col h-[700px] max-h-[90vh]">

              <div className="p-8 border-b border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Nieuwe Vacature</h2>
                  <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-red-600">
                    <X size={24} />
                  </button>
                </div>
                <p className="text-sm text-slate-500 font-medium">Kies hoe je wilt starten.</p>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-100 px-8">
                <button
                  onClick={() => setCreationMode('scratch')}
                  className={`pb - 4 pt - 4 text - xs font - black uppercase tracking - widest transition - all border - b - 2 ${creationMode === 'scratch' ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'} `}
                >
                  <Plus size={14} className="inline mr-2" /> Start Blank
                </button>
                <button
                  onClick={() => setCreationMode('text')}
                  className={`pb - 4 pt - 4 text - xs font - black uppercase tracking - widest transition - all border - b - 2 ml - 6 ${creationMode === 'text' ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'} `}
                >
                  <FileText size={14} className="inline mr-2" /> Start met Tekst
                </button>
                <button
                  onClick={() => setCreationMode('copy')}
                  className={`pb - 4 pt - 4 text - xs font - black uppercase tracking - widest transition - all border - b - 2 ml - 6 ${creationMode === 'copy' ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'} `}
                >
                  <Layers size={14} className="inline mr-2" /> Gebruik Bestaande
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-700 text-xs font-bold">
                    <AlertCircle size={18} /> {error}
                  </div>
                )}

                {creationMode === 'scratch' && (
                  <div className="space-y-6">
                    {/* Client Selector for Admin/Recruiter */}
                    {canSelectClient && (
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Klant / Bedrijf</label>
                        <select
                          value={newVacancy.clientId}
                          onChange={(e) => setNewVacancy({ ...newVacancy, clientId: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all cursor-pointer"
                        >
                          <option value="">-- Selecteer Klant --</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Functietitel</label>
                      <div className="relative group">
                        <input
                          type="text"
                          value={newVacancy.title}
                          onChange={(e) => setNewVacancy({ ...newVacancy, title: e.target.value })}
                          placeholder="bv. Senior Frontend Developer"
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all placeholder:text-gray-400"
                        />
                        {newVacancy.title && !isSuggesting && (
                          <button
                            onClick={handleMagicFill}
                            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white p-2 rounded-xl text-[#e51b23] hover:bg-red-50 transition-colors shadow-sm border border-red-100 flex items-center gap-2"
                            title="AI Auto-fill"
                          >
                            <Sparkles size={16} /> <span className="text-[10px] font-bold uppercase hidden sm:inline">AI Fill</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Categorie</label>
                        <input
                          type="text"
                          list="categories-list"
                          value={newVacancy.category}
                          onChange={(e) => setNewVacancy({ ...newVacancy, category: e.target.value })}
                          placeholder="IT, Sales..."
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                        <datalist id="categories-list">
                          <option value="IT & Engineering" />
                          <option value="Sales & Marketing" />
                          <option value="Management" />
                          <option value="Finance" />
                        </datalist>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Tags</label>
                        <input
                          type="text"
                          value={newVacancy.tags}
                          onChange={(e) => setNewVacancy({ ...newVacancy, tags: e.target.value })}
                          placeholder="React, Lead..."
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Key Skills (AI Context)</label>
                      <input
                        type="text"
                        value={newVacancy.keywords}
                        onChange={(e) => setNewVacancy({ ...newVacancy, keywords: e.target.value })}
                        placeholder="bv. React, TypeScript, Leidinggevende ervaring"
                        className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Specifieke Eisen</label>
                      <textarea
                        value={newVacancy.suggestions}
                        onChange={(e) => setNewVacancy({ ...newVacancy, suggestions: e.target.value })}
                        placeholder="Specifieke must-haves?"
                        className="w-full h-32 p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] resize-none transition-all"
                      />
                    </div>

                    {/* New Fields Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Deadline</label>
                        <input
                          type="datetime-local"
                          value={newVacancy.deadline || ''}
                          onChange={(e) => setNewVacancy({ ...newVacancy, deadline: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Startdatum</label>
                        <input
                          type="date"
                          value={newVacancy.startDate || ''}
                          onChange={(e) => setNewVacancy({ ...newVacancy, startDate: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Locatie</label>
                        <input
                          type="text"
                          placeholder="bv. Amsterdam Hybride"
                          value={newVacancy.location || ''}
                          onChange={(e) => setNewVacancy({ ...newVacancy, location: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Uren p/w</label>
                        <input
                          type="text"
                          placeholder="bv. 32-40 uur"
                          value={newVacancy.hoursPerWeek || ''}
                          onChange={(e) => setNewVacancy({ ...newVacancy, hoursPerWeek: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Looptijd / Duur</label>
                        <input
                          type="text"
                          placeholder="bv. 12 maanden, Onbepaalde tijd"
                          value={newVacancy.duration || ''}
                          onChange={(e) => setNewVacancy({ ...newVacancy, duration: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {creationMode === 'text' && (
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500 font-medium">Plak hier je bestaande vacaturetekst. Onze AI analyseert de tekst en maakt de vacature aan, waarna je nog steeds de Research & Persona tools kunt gebruiken op basis van deze tekst.</p>

                    {canSelectClient && (
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Klant / Bedrijf</label>
                        <select
                          value={newVacancy.clientId}
                          onChange={(e) => setNewVacancy({ ...newVacancy, clientId: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all cursor-pointer"
                        >
                          <option value="">-- Selecteer Klant --</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Vacature Tekst</label>
                      <textarea
                        value={pastedText}
                        onChange={(e) => setPastedText(e.target.value)}
                        placeholder="Plak hier de volledige tekst..."
                        className="w-full h-64 p-4 bg-gray-50 border-0 rounded-2xl text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] resize-none transition-all"
                      />
                    </div>
                  </div>
                )}

                {creationMode === 'copy' && (
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500 font-medium">Selecteer een bestaande vacature om te kopiëren. AI-data (Onderzoek, Persona) wordt gereset zodat je deze opnieuw kunt genereren.</p>
                    {canSelectClient && (
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Kopieer naar Klant</label>
                        <select
                          value={newVacancy.clientId}
                          onChange={(e) => setNewVacancy({ ...newVacancy, clientId: e.target.value })}
                          className="w-full p-4 bg-gray-50 border-0 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23] transition-all cursor-pointer"
                        >
                          <option value="">-- Selecteer Klant (Standaard: Huidig) --</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {vacancies.length === 0 ? (
                        <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
                          <p className="text-xs font-bold text-slate-400">Geen vacatures om te kopiëren.</p>
                        </div>
                      ) : (
                        vacancies.map(v => (
                          <div
                            key={v.id}
                            onClick={() => setSelectedCopyId(v.id)}
                            className={`p - 4 rounded - xl border cursor - pointer transition - all flex items - center justify - between group ${selectedCopyId === v.id ? 'border-[#e51b23] bg-red-50' : 'border-slate-100 bg-white hover:border-slate-300'} `}
                          >
                            <div>
                              <h4 className={`text - sm font - bold ${selectedCopyId === v.id ? 'text-[#e51b23]' : 'text-slate-900'} `}>{v.title}</h4>
                              <p className="text-[10px] text-slate-500 font-medium">{(v as any).client?.name || 'Client'}</p>
                            </div>
                            {selectedCopyId === v.id && <div className="w-4 h-4 bg-[#e51b23] rounded-full flex items-center justify-center text-white"><div className="w-1.5 h-1.5 bg-white rounded-full"></div></div>}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-8 border-t border-slate-100 bg-white">
                <button
                  onClick={handleCreate}
                  disabled={(creationMode === 'scratch' && !newVacancy.title) || (creationMode === 'copy' && !selectedCopyId) || (creationMode === 'text' && !pastedText) || isSuggesting || isCreating}
                  className="w-full flex items-center justify-center gap-3 bg-[#0F172A] hover:bg-black text-white p-4 rounded-2xl shadow-lg transition-all font-black uppercase text-xs tracking-widest disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                >
                  {isCreating ? <Loader2 size={18} className="animate-spin" /> :
                    (creationMode === 'copy' ? <Copy size={18} /> : creationMode === 'text' ? <Sparkles size={18} /> : <Send size={18} />)
                  }
                  <span>
                    {creationMode === 'copy' ? 'Dupliceer & Start AI Flow' :
                      creationMode === 'text' ? 'Analyseer & Start' :
                        'Lanceer Campagne'}
                  </span>
                </button>
              </div>

              {isSuggesting && (
                <div className="absolute inset-0 bg-white/95 flex items-center justify-center z-10 rounded-[2rem]">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 size={40} className="animate-spin text-[#e51b23]" />
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Froukje denkt na...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Support Scheduler Modal */}
      {showScheduler && (
        <SupportSchedulerModal
           onClose={() => setShowScheduler(false)}
           prefillName={user?.name || ''}
           prefillEmail={user?.email || ''}
        />
      )}
      </div>{/* end flex-1 inner */}

    </div>
  );
};

export default VacancyList;

